import { useEffect, useState } from 'react';
import {
  Plane,
  Calendar,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Timer,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { flightApi, syncApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { FlightStats, SyncStatus, MonthFlightHours, RouteStatistics, UpcomingLeg } from '@/types';
import { format, parseISO } from 'date-fns';
import { BaseAirportModal } from '@/components/dashboard/BaseAirportModal';

export function DashboardPage() {
  const [stats, setStats] = useState<FlightStats | null>(null);
  const [monthHours, setMonthHours] = useState<MonthFlightHours | null>(null);
  const [routeStats, setRouteStats] = useState<RouteStatistics | null>(null);
  const [upcomingLegs, setUpcomingLegs] = useState<UpcomingLeg[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const EXCLUDE_BASE_KEY = 'dashboard-exclude-base';

  const [routePeriod, setRoutePeriod] = useState<'week' | 'month' | 'year'>('week');
  const [excludeBase, setExcludeBase] = useState(() => {
    try {
      return localStorage.getItem(EXCLUDE_BASE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showBaseModal, setShowBaseModal] = useState(false);

  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingMonthHours, setIsLoadingMonthHours] = useState(true);
  const [isLoadingRouteStats, setIsLoadingRouteStats] = useState(true);
  const [isLoadingUpcomingLegs, setIsLoadingUpcomingLegs] = useState(true);
  const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState(true);

  const { user } = useAuthStore();

  useEffect(() => {
    try {
      localStorage.setItem(EXCLUDE_BASE_KEY, String(excludeBase));
    } catch {
      // ignore
    }
  }, [excludeBase]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingStats(true);
      setIsLoadingMonthHours(true);
      setIsLoadingUpcomingLegs(true);
      setIsLoadingSyncStatus(true);

      try {
        const [statsRes, monthHoursRes, upcomingLegsRes, syncRes] = await Promise.all([
          flightApi.getStats(),
          flightApi.getMonthHours(),
          flightApi.getUpcomingLegs(),
          syncApi.getStatus(),
        ]);

        setStats(extractData<FlightStats>(statsRes.data));
        setMonthHours(extractData<MonthFlightHours>(monthHoursRes.data));
        setUpcomingLegs(extractData<UpcomingLeg[]>(upcomingLegsRes.data) || []);
        setSyncStatus(extractData<SyncStatus>(syncRes.data));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoadingStats(false);
        setIsLoadingMonthHours(false);
        setIsLoadingUpcomingLegs(false);
        setIsLoadingSyncStatus(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadRouteStats = async () => {
      setIsLoadingRouteStats(true);
      try {
        const res = await flightApi.getRouteStats(routePeriod, excludeBase);
        setRouteStats(extractData<RouteStatistics>(res.data));
      } catch (error) {
        console.error('Failed to fetch route stats:', error);
      } finally {
        setIsLoadingRouteStats(false);
      }
    };

    loadRouteStats();
  }, [routePeriod, excludeBase, user?.baseAirport]);

  // Helper to extract data from various response formats
  const extractData = <T,>(responseData: any): T | null => {
    if (responseData === null || responseData === undefined) {
      return null;
    }
    // If it's a wrapped response: { success, data }
    if (responseData.data !== undefined) {
      return responseData.data as T;
    }
    // Direct data
    return responseData as T;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return '同步成功';
      case 'failed':
        return '同步失败';
      default:
        return '等待同步';
    }
  };

  const renderMetric = (value: React.ReactNode, isLoading: boolean) => (
    <div className="text-2xl font-bold min-h-[2rem] flex items-center">
      {isLoading ? (
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
      ) : (
        value
      )}
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">仪表盘</h1>
          <p className="text-gray-500 mt-1">
            欢迎回来，{user?.displayName || user?.username}
          </p>
        </div>
      </div>

      {/* Route Statistics Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            看看我都飞了哪里
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <Tabs
              value={routePeriod}
              onValueChange={(value) => setRoutePeriod(value as 'week' | 'month' | 'year')}
            >
              <TabsList>
                <TabsTrigger value="week">周</TabsTrigger>
                <TabsTrigger value="month">月</TabsTrigger>
                <TabsTrigger value="year">年</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="exclude-base"
                  checked={excludeBase}
                  onCheckedChange={setExcludeBase}
                />
                <label htmlFor="exclude-base" className="text-sm text-gray-600 cursor-pointer">
                  排除基地
                </label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  基地: {user?.baseAirport || '未设置'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBaseModal(true)}
                >
                  {user?.baseAirport ? '修改' : '设置'}
                </Button>
              </div>
            </div>
          </div>

          {isLoadingRouteStats ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              加载中...
            </div>
          ) : (routeStats?.topAirports ?? []).length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p>该周期内暂无航线数据</p>
            </div>
          ) : (
            <div className="space-y-3">
              {routeStats?.topAirports.map((item, index) => (
                <div
                  key={item.airportCode}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="font-medium">
                      {item.airportName || item.airportCode}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {item.landingCount} 次
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">今日航班</CardTitle>
            <Plane className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {renderMetric(stats?.today ?? 0, isLoadingStats)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">本周航班</CardTitle>
            <Calendar className="w-4 h-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {renderMetric(stats?.thisWeek ?? 0, isLoadingStats)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">本月航班</CardTitle>
            <TrendingUp className="w-4 h-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            {renderMetric(stats?.thisMonth ?? 0, isLoadingStats)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">总航班数</CardTitle>
            <Plane className="w-4 h-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            {renderMetric(stats?.total ?? 0, isLoadingStats)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">当月飞行小时</CardTitle>
            <Timer className="w-4 h-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {renderMetric(
              monthHours ? `${monthHours.totalHours}h${monthHours.totalMinutes}m` : '0h0m',
              isLoadingMonthHours
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Flights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5" />
              即将开始的航班
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingUpcomingLegs ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                加载中...
              </div>
            ) : upcomingLegs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Plane className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>暂无即将开始的航班</p>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingLegs.map((leg) => (
                  <div
                    key={leg.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{leg.flightNo || 'N/A'}</span>
                        {leg.acNo && (
                          <span className="text-sm text-gray-500">机号: {leg.acNo}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {leg.depCode} {leg.depName} → {leg.arrCode} {leg.arrName}
                      </p>
                      {leg.signTime && (
                        <p className="text-sm text-amber-600 mt-1">
                          签到: {format(parseISO(leg.signTime), 'MM-dd HH:mm')}
                        </p>
                      )}
                      {leg.postName && (
                        <p className="text-sm text-gray-500 mt-1">岗位: {leg.postName}</p>
                      )}
                    </div>
                    <div className="text-right ml-4 shrink-0">
                      <p className="font-medium">
                        {leg.planDepTime && format(parseISO(leg.planDepTime), 'MM-dd HH:mm')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {leg.planArrTime && format(parseISO(leg.planArrTime), 'MM-dd HH:mm')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sync Status & ICS */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                同步状态
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSyncStatus ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  加载中...
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {syncStatus?.lastSync ? getStatusIcon(syncStatus.lastSync.status) : <Clock className="w-5 h-5 text-gray-400" />}
                      <div>
                        <p className="font-medium">
                          {syncStatus?.lastSync ? getStatusText(syncStatus.lastSync.status) : '尚未同步'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {syncStatus?.lastSync?.completedAt
                            ? format(parseISO(syncStatus.lastSync.completedAt), 'yyyy-MM-dd HH:mm')
                            : '从未同步'}
                        </p>
                      </div>
                    </div>
                    {syncStatus?.lastSync && (
                      <div className="text-right text-sm text-gray-500">
                        <p>{syncStatus.lastSync.flightsTotal ?? 0} 个航班</p>
                        <p>{syncStatus.lastSync.dailyTotal ?? 0} 个事项</p>
                      </div>
                    )}
                  </div>

                  {syncStatus?.nextScheduledSync && (
                    <p className="text-sm text-gray-500">
                      下次自动同步: {format(parseISO(syncStatus.nextScheduledSync), 'yyyy-MM-dd HH:mm')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      <BaseAirportModal
        isOpen={showBaseModal}
        onClose={() => setShowBaseModal(false)}
      />
    </div>
  );
}
